import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Clock, User, ChevronRight } from 'lucide-react'
import type { Case } from '../types'
import { SeverityBadge } from './SeverityBadge'
import { StatusBadge } from './StatusBadge'
import { timeAgo } from '../lib/utils'

interface CaseCardProps {
  caseItem: Case
  index: number
}

export function CaseCard({ caseItem, index }: CaseCardProps) {
  const navigate = useNavigate()

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, x: -20 }}
      transition={{ duration: 0.25, delay: index * 0.05 }}
      className="bg-white rounded-xl border border-gray-200 p-4 hover:shadow-md transition-shadow cursor-pointer"
      onClick={() => navigate(`/analyst/case/${caseItem.id}`)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          navigate(`/analyst/case/${caseItem.id}`)
        }
      }}
      role="listitem"
      tabIndex={0}
      aria-label={`Case ${caseItem.case_number}: ${caseItem.title}`}
    >
      {/* Top row: case number + severity + status */}
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <span className="text-xs font-mono text-gray-500">{caseItem.case_number}</span>
        <SeverityBadge severity={caseItem.severity} size="sm" />
        <StatusBadge status={caseItem.status} size="sm" />
        <div className="ml-auto flex items-center gap-1 text-xs text-gray-400">
          <Clock className="w-3 h-3" aria-hidden="true" />
          <time dateTime={caseItem.created_at}>{timeAgo(caseItem.created_at)}</time>
        </div>
      </div>

      {/* Title */}
      <h3 className="text-base font-semibold text-navy mb-1">{caseItem.title}</h3>

      {/* Description */}
      {caseItem.description && (
        <p className="text-sm text-gray-600 line-clamp-2 mb-2">{caseItem.description}</p>
      )}

      {/* Bottom row: principal + platform + investigate link */}
      <div className="flex items-center justify-between mt-3 pt-2 border-t border-gray-100">
        <div className="flex items-center gap-3 text-xs text-gray-500">
          <span className="flex items-center gap-1">
            <User className="w-3 h-3" aria-hidden="true" />
            {caseItem.principal_email ?? 'Unknown'}
          </span>
          {caseItem.platform && (
            <span className="px-1.5 py-0.5 bg-gray-100 rounded text-xs capitalize">
              {caseItem.platform}
            </span>
          )}
        </div>
        <span className="flex items-center gap-1 text-xs font-medium text-brass">
          Investigate
          <ChevronRight className="w-3 h-3" aria-hidden="true" />
        </span>
      </div>
    </motion.div>
  )
}
